import { ChevronDown, Building2, Check, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-context";
import { useScope } from "@/contexts/scope-context";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";

interface Project {
  id: string;
  name: string;
  brand_count: number;
}

interface ProjectsResponse {
  projects: Project[];
}

function canCreateProjects(role: string | null | undefined) {
  return role === "analyst" || role === "admin" || role === "owner";
}

export function ScopeSwitcher() {
  const { workspace, startupProject } = useAuth();
  const { projectId, setProjectId } = useScope();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const startupProjectMatchesCurrent =
    !!projectId && startupProject?.id === projectId;
  const needsProjectListForClosedLabel =
    !!projectId && !startupProjectMatchesCurrent;
  const shouldLoadProjects =
    !!workspace?.id && (open || needsProjectListForClosedLabel);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.workspaces.projects(workspace?.id || ""),
    queryFn: () =>
      api<ProjectsResponse>(`/workspaces/${workspace?.id}/projects`),
    enabled: shouldLoadProjects,
    staleTime: 60 * 1000,
  });

  const projects = data?.projects || [];
  const currentProject = projects.find((p) => p.id === projectId);
  const projectLabel =
    currentProject?.name ||
    (startupProjectMatchesCurrent ? startupProject?.name : null) ||
    "Select project";

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 rounded-[var(--radius-sm)] px-3 h-10 text-sm text-text-1 hover:bg-overlay-hover transition-colors focus-ring max-w-[240px]",
        )}
        style={{ transitionDuration: "var(--transition-duration-fast)" }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Switch project"
      >
        <Building2
          className="w-4 h-4 shrink-0 text-text-2"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span className="truncate font-medium">{projectLabel}</span>
        <ChevronDown
          className="w-4 h-4 shrink-0 text-text-3"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-[320px] rounded-[var(--radius-md)] border border-border-1 bg-surface-3 shadow-2 z-[var(--z-popover)] overflow-hidden"
          role="listbox"
          aria-label="Projects"
        >
          {/* Workspace label */}
          <div className="px-3 pt-3 pb-1">
            <span className="text-[11px] font-medium uppercase tracking-wider text-text-3">
              {workspace?.name || "Workspace"}
            </span>
          </div>

          {/* Project list */}
          <div className="max-h-[320px] overflow-y-auto p-1.5">
            {projects.map((project) => (
              <button
                key={project.id}
                role="option"
                aria-selected={project.id === projectId}
                onClick={() => {
                  setProjectId(project.id);
                  setOpen(false);
                }}
                className={cn(
                  "flex items-center justify-between w-full rounded-[var(--radius-sm)] px-3 py-2 text-sm text-left transition-colors",
                  project.id === projectId
                    ? "bg-primary-surface text-primary"
                    : "text-text-1 hover:bg-overlay-hover",
                )}
                style={{
                  transitionDuration: "var(--transition-duration-fast)",
                }}
              >
                <span className="truncate">{project.name}</span>
                {project.id === projectId && (
                  <Check className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                )}
              </button>
            ))}

            {isLoading && projects.length === 0 && (
              <div className="px-3 py-4 text-sm text-text-3 text-center">
                Loading projects…
              </div>
            )}

            {!isLoading && projects.length === 0 && (
              <div className="px-3 py-4 text-sm text-text-3 text-center">
                No projects available
              </div>
            )}
          </div>

          {/* Add project */}
          {canCreateProjects(workspace?.role) && (
            <div className="border-t border-border-1 p-1.5">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/projects/new");
                }}
                className="flex sm:flex-row items-center gap-2 w-full rounded-[var(--radius-sm)] px-3 py-2 text-sm text-text-2 hover:bg-overlay-hover hover:text-text-1 transition-colors cursor-pointer"
                style={{
                  transitionDuration: "var(--transition-duration-fast)",
                }}
              >
                <Plus
                  className="w-4 h-4 shrink-0"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <span>New Project</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
